/** @file Ear-clipping triangulation of a simple counter-clockwise polygon (the first n flat points). */
import { throwError } from '@pose2d/format/throwError';
import { flatOrient } from './flatOrient';
import { ringArea } from './ringArea';

/** VALIDATION at args.attachment (autoMesh's input): the outline handed to the triangulation cannot be meshed. */
function degenerate(): never {
  return throwError('VALIDATION', {}, {
    path: 'args.attachment',
    hint: 'the mesh outline is degenerate (fewer than 3 points, no area or clockwise), so it cannot be triangulated',
  });
}

function contains(p: ArrayLike<number>, a: number, b: number, c: number, q: number): boolean {
  return flatOrient(p, a, b, q) >= 0 && flatOrient(p, b, c, q) >= 0 && flatOrient(p, c, a, q) >= 0;
}

function isEar(p: ArrayLike<number>, ring: number[], k: number): boolean {
  const m = ring.length;
  const a = ring[(k + m - 1) % m] ?? 0;
  const b = ring[k] ?? 0;
  const c = ring[(k + 1) % m] ?? 0;
  if (flatOrient(p, a, b, c) <= 0) return false;
  for (const q of ring) {
    if (q !== a && q !== b && q !== c && contains(p, a, b, c, q)) return false;
  }
  return true;
}

/**
 * First ear in ring order; when rounding hides every ear, the most convex corner, so clipping always progresses
 * (VALIDATION when no corner is convex: the remaining polygon is not counter-clockwise).
 */
function nextEar(p: ArrayLike<number>, ring: number[]): number {
  const m = ring.length;
  let best = 0;
  let bestTurn = -Infinity;
  for (let k = 0; k < m; k++) {
    if (isEar(p, ring, k)) return k;
    const turn = flatOrient(p, ring[(k + m - 1) % m] ?? 0, ring[k] ?? 0, ring[(k + 1) % m] ?? 0);
    if (turn > bestTurn) {
      best = k;
      bestTurn = turn;
    }
  }
  return bestTurn > 0 ? best : degenerate();
}

/**
 * n − 2 counter-clockwise triangles (flat index triples) covering the polygon of points 0..n−1 of `p`;
 * VALIDATION at args.attachment when n < 3 or the polygon's signed area is not positive.
 */
export function earClip(p: ArrayLike<number>, n: number): number[] {
  if (n < 3 || ringArea(Array.from({ length: 2 * n }, (_, i) => p[i] ?? 0)) <= 0) degenerate();
  const ring = Array.from({ length: n }, (_, i) => i);
  const triangles: number[] = [];
  while (ring.length > 3) {
    const m = ring.length;
    const k = nextEar(p, ring);
    triangles.push(ring[(k + m - 1) % m] ?? 0, ring[k] ?? 0, ring[(k + 1) % m] ?? 0);
    ring.splice(k, 1);
  }
  triangles.push(ring[0] ?? 0, ring[1] ?? 0, ring[2] ?? 0);
  return triangles;
}
